import rangy from 'rangy';
import 'rangy/lib/rangy-classapplier';
import {
  attachVariantMarkerToQuestionHtml,
  normalizeKatexMarkupForPersistence,
} from '../../../../../../utils/vignetteSchedulingGuards';
import {
  COACH_HIGHLIGHT_CLASS,
  stripCoachHighlightsFromHtml,
} from '../QuestionCoach/coachHighlights';

/**
 * The DOM side of question-stem highlighting, shared by the QBank exam view
 * (`useHighlighting`) and the in-chat question block (ORA-820). Every helper
 * works inside a given container, so a caller decides where highlighting is
 * allowed; none of them binds listeners or persists anything.
 */

export const HIGHLIGHT_CLASS = 'highlight';

// Re-exported for existing callers (`highlighting.ts`, `useChatQuestionMarks.ts`),
// which import it from here. It lives in `vignetteSchedulingGuards.ts` because
// that module's staleness check needs it too, to read the TeX source back out
// of older saved copies that still hold rendered KaTeX markup.
export { normalizeKatexMarkupForPersistence };

/**
 * The highlighted stem as it is stored: Question Coach marks (ORA-1091)
 * removed, TeX restored, variant marker on.
 */
export const buildPersistableQuestionHighlights = ({
  html,
  variantId,
}: {
  html: string;
  variantId: string;
}): string =>
  attachVariantMarkerToQuestionHtml(
    normalizeKatexMarkupForPersistence(stripCoachHighlightsFromHtml(html)),
    variantId
  );

/**
 * The highlight span an event landed on, when it is inside `container`.
 * Accepts a text node as well as an element.
 */
export const resolveHighlightElement = (
  container: HTMLElement | null,
  target: EventTarget | null
): HTMLElement | null => {
  const targetElement =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;

  if (!targetElement) {
    return null;
  }

  const candidate = targetElement.classList.contains(HIGHLIGHT_CLASS)
    ? targetElement
    : targetElement.closest(`.${HIGHLIGHT_CLASS}`);

  if (!candidate) {
    return null;
  }

  if (!container?.contains(candidate)) {
    return null;
  }

  return candidate instanceof HTMLElement ? candidate : null;
};

const resolveTopLevelHighlightElement = (element: HTMLElement) => {
  let current = element;

  let parentHighlight =
    current.parentElement?.closest(`.${HIGHLIGHT_CLASS}`) ?? null;

  while (parentHighlight instanceof HTMLElement) {
    current = parentHighlight;

    parentHighlight =
      current.parentElement?.closest(`.${HIGHLIGHT_CLASS}`) ?? null;
  }

  return current;
};

const hasGapBetweenHighlights = (left: HTMLElement, right: HTMLElement) => {
  const range = document.createRange();
  range.setStartAfter(left);
  range.setEndBefore(right);

  const fragment = range.cloneContents();

  fragment.querySelectorAll(`span.${HIGHLIGHT_CLASS}`).forEach((node) => {
    node.remove();
  });

  const textBetween = (fragment.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .trim();

  return textBetween.length > 0;
};

// One visual highlight can be several spans (the class applier splits at
// element boundaries); removing one should remove the run it belongs to.
const resolveContiguousHighlightGroup = (
  container: HTMLElement | null,
  element: HTMLElement
) => {
  if (!container) {
    return [element];
  }

  const topLevelHighlights = Array.from(
    container.querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}`)
  ).filter((node) => !node.parentElement?.closest(`.${HIGHLIGHT_CLASS}`));

  const topLevelElement = resolveTopLevelHighlightElement(element);
  const targetIndex = topLevelHighlights.indexOf(topLevelElement);

  if (targetIndex === -1) {
    return [topLevelElement];
  }

  let start = targetIndex;
  let end = targetIndex;

  while (
    start > 0 &&
    !hasGapBetweenHighlights(
      topLevelHighlights[start - 1],
      topLevelHighlights[start]
    )
  ) {
    start -= 1;
  }

  while (
    end < topLevelHighlights.length - 1 &&
    !hasGapBetweenHighlights(
      topLevelHighlights[end],
      topLevelHighlights[end + 1]
    )
  ) {
    end += 1;
  }

  return topLevelHighlights.slice(start, end + 1);
};

/** Unwraps the highlight run `element` belongs to and clears the selection. */
export const removeHighlightGroup = (
  container: HTMLElement | null,
  element: HTMLElement
): void => {
  const contiguousHighlights = resolveContiguousHighlightGroup(
    container,
    element
  );

  contiguousHighlights.forEach((highlightNode) => {
    const parent = highlightNode.parentNode;
    if (!parent) {
      return;
    }

    // A Question Coach span the student also highlighted (ORA-1091) stays as
    // the coach's mark.
    if (highlightNode.classList.contains(COACH_HIGHLIGHT_CLASS)) {
      highlightNode.classList.remove(HIGHLIGHT_CLASS);
      return;
    }

    while (highlightNode.firstChild) {
      parent.insertBefore(highlightNode.firstChild, highlightNode);
    }

    if (highlightNode.parentNode === parent) {
      parent.removeChild(highlightNode);
    }
    parent.normalize();
  });

  rangy.getSelection().removeAllRanges();
};

const expandRangeToNearestTextNodes = (range: Range) => {
  if (range.startContainer.nodeType !== Node.TEXT_NODE) {
    let startNode = range.startContainer.childNodes[range.startOffset];
    while (startNode && startNode.hasChildNodes()) {
      // @ts-expect-error - TS doesn't recognize hasChildNodes
      startNode = startNode.firstChild;
    }
    range.setStartBefore(startNode || range.startContainer);
  }

  if (range.endContainer.nodeType !== Node.TEXT_NODE) {
    let endNode = range.endContainer.childNodes[range.endOffset];
    while (endNode && endNode.hasChildNodes()) {
      // @ts-expect-error - TS doesn't recognize hasChildNodes
      endNode = endNode.lastChild;
    }
    range.setEndAfter(endNode || range.endContainer);
  }

  return range;
};

/**
 * Wraps the current selection in highlight spans when it sits inside
 * `container`, then clears it. Returns whether anything was highlighted.
 */
export const applyHighlightToSelection = (
  container: HTMLElement | null
): boolean => {
  const selection = rangy.getSelection();
  if (!selection.rangeCount || selection.isCollapsed) {
    return false;
  }

  let range = selection.getRangeAt(0);
  if (!range.toString().trim().length) {
    return false;
  }

  // Check if the selection is within the highlight target
  if (!container?.contains(range.commonAncestorContainer)) {
    return false;
  }

  // Adjust range to contain only text nodes by expanding to nearest text node boundaries
  // @ts-expect-error - rangy's range is not typed as a DOM Range
  range = expandRangeToNearestTextNodes(range);

  // Check if the range is now properly adjusted
  if (
    range.startContainer.nodeType !== Node.TEXT_NODE ||
    range.endContainer.nodeType !== Node.TEXT_NODE
  ) {
    console.error('Range still includes non-text node elements.');
    return false;
  }

  // @ts-expect-error - TS doesn't recognize createClassApplier
  const cssClassApplier = rangy.createClassApplier(HIGHLIGHT_CLASS, {
    elementTagName: 'span',
    normalize: true,
  });

  cssClassApplier.applyToRange(range);

  // Clear the selection
  rangy.getSelection().removeAllRanges();

  return true;
};
